import {
  Button,
  ButtonGroup,
  chakra,
  Flex,
  FlexProps,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  Spinner,
  Text,
  useColorModeValue,
  useMergeRefs,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  RiAddLine,
  RiCheckboxBlankLine,
  RiCheckboxFill,
} from "@remixicon/react";
import uniq from "lodash/uniq";
import React, {
  ForwardedRef,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import FormSubmit from "@/common/components/Form/FormSubmit";
import FormTextArea from "@/common/components/Form/FormTextArea";
import { IcoSearch } from "@/common/components/Icons";
import { SwitchButtonGroup } from "@/common/components/SwitchButtonGroup";
import useInputWithSelectableListItems from "@/common/hooks/useInputWithSelectableListItems";
import { parseIDList } from "@/common/utils/parseIDList";
import { z } from "@reflag/shared/utils/zod";

const AddIcon = chakra(RiAddLine);

const MAX_ID_LENGTH = 100;
const MIN_LIST_HEIGHT = 96;
const MAX_LIST_HEIGHT = "40vh";
const LIST_ITEM_HEIGHT = 28;

export const BulkInputSchema = z.object({
  ids: z.string().refine((value) => {
    return !parseIDList(value).find((id) => id.length > MAX_ID_LENGTH);
  }, `Single IDs must be less than ${MAX_ID_LENGTH} characters. Did you correctly separate IDs with commas, space or line breaks?`),
});

export type BulkInputForm = z.input<typeof BulkInputSchema>;

export type ListType = "all" | "selected";

export interface MultiSelectorItem {
  id: string;
  [key: string]: any;
  isUnknown?: boolean;
}

const sharedListItemProps = {
  alignItems: "center",
  cursor: "pointer",
  display: "flex",
  fontSize: "sm",
  gap: 2,
  py: 1,
  px: 2,
};

export type MultiSelectorBaseProps<T extends MultiSelectorItem> = {
  value?: string[];
  footer?: ReactNode;
  onChange?: (itemIds: string[], meta?: T[]) => void;

  // Data and loading states
  suggestions?: T[];
  isLoadingSuggestions?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;

  // Search functionality
  listType?: ListType;
  onListTypeChange?: (listType: ListType) => void;
  searchValue?: string;
  onSearchInput?: (searchValue: string) => void;

  // Display component for items
  itemRenderFn: (item: T, search?: string) => ReactNode;

  // UI labels
  searchPlaceholder?: string;
  addLabel?: string;
  noItemsFound?: string | null;
  addRawLabel?: (searchValue: string) => ReactNode;

  // Allow manual ID entry
  allowManualEntry?: boolean;

  // All/selected toggle (default: true)
  allSelectedToggle?: boolean;
};

export type MultiSelectorProps<T extends MultiSelectorItem> =
  MultiSelectorBaseProps<T> & {
    isReadOnly?: boolean;
  } & Omit<FlexProps, "children" | "onChange">;

const defaultAddRawLabel = (searchValue: string) => (
  <>
    Add{" "}
    <Text as="span" fontWeight="bold">
      {searchValue}
    </Text>
  </>
);

function MultiSelectorInner<T extends MultiSelectorItem>(
  {
    value,
    onChange: onChangeProp,
    suggestions = [],
    isLoadingSuggestions = false,
    hasMore,
    onLoadMore,
    isLoadingMore = false,
    listType,
    onListTypeChange,
    searchValue,
    onSearchInput,
    itemRenderFn,
    searchPlaceholder = "Search items",
    noItemsFound = "No items found",
    allowManualEntry = true,
    allSelectedToggle = true,
    isReadOnly = false,
    addRawLabel = defaultAddRawLabel,
    ...rest
  }: MultiSelectorProps<T>,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedInputRef = useMergeRefs(ref, inputRef);
  const hasValue = value && value.length > 0;
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const bgHoverColor = useColorModeValue("gray.50", "gray.700");

  const bulkForm = useForm<BulkInputForm>({
    resolver: zodResolver(BulkInputSchema),
  });

  const handleBulkSubmit = bulkForm.handleSubmit(({ ids }) => {
    onChangeProp?.(uniq((value ?? []).concat(parseIDList(ids))));
    setShowBulkAdd(false);
    bulkForm.reset();
    setTimeout(() => {
      inputRef.current?.focus();
    });
  });

  const showAdd = useMemo(() => {
    return (
      allowManualEntry &&
      searchValue &&
      !value?.includes(searchValue) &&
      !suggestions.some((s) => Object.values(s).some((v) => v === searchValue))
    );
  }, [allowManualEntry, searchValue, value, suggestions]);

  const listHeight = useMemo(() => {
    // crude approximation of the list height (can't use ref as it gets unmounted)
    return Math.max(
      suggestions.length * LIST_ITEM_HEIGHT + (showAdd ? LIST_ITEM_HEIGHT : 0),
      MIN_LIST_HEIGHT,
    );
  }, [suggestions.length, showAdd]);

  const listCount = useMemo(() => {
    return suggestions.length + (hasMore ? 1 : 0) + (showAdd ? 1 : 0);
  }, [suggestions.length, showAdd, hasMore]);

  const loadMoreIndex = useMemo(() => {
    return listCount - 1 - (showAdd ? 1 : 0);
  }, [listCount, showAdd]);

  const addIndex = useMemo(() => {
    return listCount - 1;
  }, [listCount]);

  const onChange = useCallback(
    (item: T, isChecked: boolean) => {
      onChangeProp?.(
        isChecked
          ? (value ?? []).filter((id) => id !== item.id)
          : uniq((value ?? []).concat(item.id)),
        [item],
      );
    },
    [onChangeProp, value],
  );

  const addItemRaw = useCallback(
    (item: T) => {
      onChangeProp?.(uniq((value ?? []).concat(item.id)), [item]);
      onSearchInput?.("");
    },
    [onChangeProp, value, onSearchInput],
  );

  const onPickSelected = useCallback(
    (selected: number, setSelected: (selected: number) => void) => {
      if (hasMore && selected === loadMoreIndex) {
        return onLoadMore?.();
      }
      if (showAdd && selected === addIndex) {
        setSelected(selected - 1);
        return addItemRaw({ id: searchValue!, isUnknown: true } as T);
      }
      const item = suggestions[selected];
      const isChecked = value?.includes(item.id);
      onChange(item, isChecked ?? false);
    },
    [
      hasMore,
      loadMoreIndex,
      showAdd,
      addIndex,
      suggestions,
      value,
      onChange,
      onLoadMore,
      searchValue,
      addItemRaw,
    ],
  );

  const { selected, setSelected, onKeyDown } = useInputWithSelectableListItems({
    isOpen: true,
    maxItems: listCount,
    resetOnMaxItemsChange: false,
    hasExactMatch: false,
    onPickSelected,
  });

  useEffect(() => {
    if (listRef.current) {
      listRef.current.children[selected]?.scrollIntoView({
        behavior: "auto",
        block: "nearest",
      });
    }
  }, [selected]);

  return (
    <Flex
      borderBottom="1px solid"
      borderBottomColor="appBorder"
      direction="column"
      maxW="lg"
      minW="xs"
      {...rest}
    >
      {showBulkAdd ? (
        <FormProvider {...bulkForm}>
          <Flex direction="column" gap={4} p={4}>
            <FormTextArea
              maxH={MAX_LIST_HEIGHT}
              minH={`min(${listHeight}px, ${MAX_LIST_HEIGHT})`}
              name="ids"
              p={4}
              placeholder="Paste IDs separated by commas, spaces or line breaks."
              w="sm"
              autoFocus
            />
            <ButtonGroup size="xs">
              <FormSubmit type="button" onClick={handleBulkSubmit}>
                Add
              </FormSubmit>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowBulkAdd(false);
                  bulkForm.reset();
                }}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </Flex>
        </FormProvider>
      ) : (
        <>
          {!isReadOnly && (
            <ButtonGroup
              justifyContent="space-between"
              p={4}
              pt={0}
              size="xs"
              variant="outline"
            >
              <HStack>
                <Restricted flagKey="add-in-bulk">
                  <Button onClick={() => setShowBulkAdd(true)}>Import</Button>
                </Restricted>
                <Button
                  isDisabled={!hasValue}
                  onClick={() => {
                    setSelected(-1);
                    onListTypeChange?.("all");
                    onChangeProp?.([]);
                    inputRef.current?.focus();
                  }}
                >
                  Deselect all
                </Button>
              </HStack>
              {allSelectedToggle && (
                <SwitchButtonGroup
                  buttons={[
                    {
                      id: "all",
                      label: "All",
                    },
                    {
                      id: "selected",
                      label: "Selected",
                    },
                  ]}
                  isDisabled={!hasValue}
                  size="xs"
                  value={listType}
                  onChange={(value) => {
                    setSelected(-1);
                    onListTypeChange?.(value);
                  }}
                />
              )}
            </ButtonGroup>
          )}
          <HStack spacing={4} w="100%">
            <InputGroup variant="flushed" w="100%">
              <InputLeftElement mx={1} pointerEvents="none">
                <IcoSearch color="dimmed" size="md" />
              </InputLeftElement>
              {isLoadingSuggestions && (
                <InputRightElement mx={1} pointerEvents="none">
                  <Spinner size="xs" variant="subtle" />
                </InputRightElement>
              )}
              <Input
                ref={mergedInputRef}
                placeholder={searchPlaceholder}
                value={searchValue}
                autoFocus
                onChange={(e) => {
                  setSelected(0); // always select the first item
                  onSearchInput?.(e.target.value);
                }}
                onKeyDown={(e) => {
                  onKeyDown?.(e);
                }}
              />
            </InputGroup>
          </HStack>
          <List
            ref={listRef}
            maxH={MAX_LIST_HEIGHT}
            overflowY="auto"
            sx={{
              mask: "linear-gradient(to bottom, transparent, black var(--top-fade) calc(100% - var(--bottom-fade)), transparent)",
              animation: "scrollfade",
              animationTimeline: "--scrollfade",
              scrollTimeline: "--scrollfade y",
            }}
          >
            {suggestions.length ? (
              <>
                {suggestions.map((item, index) => {
                  const isChecked = value?.includes(item.id);
                  return (
                    <ListItem
                      {...sharedListItemProps}
                      key={item.id}
                      _hover={{
                        bg: bgHoverColor,
                      }}
                      bg={selected === index ? bgHoverColor : undefined}
                      cursor={isReadOnly ? "default" : "pointer"}
                      height={`${LIST_ITEM_HEIGHT}px`}
                      sx={{
                        "&:hover svg": {
                          visibility: "visible",
                        },
                      }}
                      onClick={
                        isReadOnly
                          ? undefined
                          : () => {
                              onChange(item, isChecked ?? false);
                            }
                      }
                    >
                      {isChecked ? (
                        <RiCheckboxFill size={14} />
                      ) : (
                        <RiCheckboxBlankLine size={14} visibility="hidden" />
                      )}
                      {itemRenderFn(item, searchValue)}
                    </ListItem>
                  );
                })}
                {hasMore && (
                  <ListItem
                    {...sharedListItemProps}
                    _hover={{
                      bg: bgHoverColor,
                    }}
                    bg={selected === loadMoreIndex ? bgHoverColor : undefined}
                    fontSize="xs"
                    justifyContent="center"
                    onClick={() => {
                      onLoadMore?.();
                    }}
                  >
                    {isLoadingMore ? (
                      <Spinner my={1} size="xs" variant="subtle" />
                    ) : (
                      "Load more"
                    )}
                  </ListItem>
                )}
              </>
            ) : noItemsFound ? (
              <ListItem {...sharedListItemProps} cursor="default" py={2}>
                <Text color="gray.500" fontSize="sm" pl={5}>
                  {noItemsFound}
                </Text>
              </ListItem>
            ) : null}
            {showAdd && (
              <ListItem
                {...sharedListItemProps}
                _hover={{
                  bg: bgHoverColor,
                }}
                bg={selected === addIndex ? bgHoverColor : undefined}
                borderTop="1px solid"
                borderTopColor="appBorder"
                gap={1.5}
                pl={7}
                onClick={() => {
                  addItemRaw({ id: searchValue!, isUnknown: true } as T);
                }}
              >
                <AddIcon mx={1} size={16} />
                <Text>{addRawLabel(searchValue!)}</Text>
              </ListItem>
            )}
          </List>
        </>
      )}
    </Flex>
  );
}

export const MultiSelector = forwardRef(MultiSelectorInner) as (<
  T extends MultiSelectorItem,
>(
  props: MultiSelectorProps<T> & {
    ref?: ForwardedRef<HTMLInputElement>;
  },
) => ReturnType<typeof MultiSelectorInner>) & { displayName: string };

MultiSelector.displayName = "MultiSelector";
